// RecentActivityCard.tsx
import * as React from 'react';
import { alpha, styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { TrendingUpRounded } from '@mui/icons-material';

export interface RecentActivityCardProps {
	title?: string;
	className?: string;
	minHeight?: number;
}

const Root = styled(Paper)(({ theme }) => ({
	borderRadius: 20,
	border: `1px solid ${theme.palette.divider}`,
	backgroundColor: theme.palette.background.paper,
	padding: theme.spacing(2),
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	maxWidth: 1250,
}));

const Header = styled('div')(({ theme }) => ({
	padding: theme.spacing(1),
	paddingBottom: theme.spacing(2),
}));

const EmptyArea = styled('div')(({ theme }) => ({
	flex: 1,
	minHeight: 360,
	display: 'grid',
	placeItems: 'center',
	padding: theme.spacing(2),
}));

const IconRing = styled(Avatar)(({ theme }) => ({
	width: 72,
	height: 72,
	backgroundColor:
		theme.palette.mode === 'light'
			? alpha(theme.palette.text.primary, 0.06)
			: alpha(theme.palette.common.white, 0.08),
	color: theme.palette.primary.main,
	boxShadow:
		theme.palette.mode === 'light'
			? `inset 0 0 0 1px ${alpha(theme.palette.text.primary, 0.08)}`
			: `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.12)}`,
}));

const ProTip = styled(Box)(({ theme }) => ({
	marginTop: theme.spacing(3),
	borderRadius: theme.shape.borderRadius * 2,
	padding: theme.spacing(1.5, 2),
	backgroundColor: '#ececf0',
	border: `1px solid #e8e8e8`,
	maxWidth: 680,
	width: '100%',
	marginLeft: 'auto',
	marginRight: 'auto',
}));

const Strong = styled('span')(({ theme }) => ({
	fontWeight: 600,
}));

export const RecentActivityCard = ({
	title = 'Recent Activity',
	className,
	minHeight,
}: RecentActivityCardProps) => {
	return (
		<Root
			elevation={0}
			className={className}
			role="region"
			aria-label={title}
		>
			<Header>
				<Typography variant="subtitle1">{title}</Typography>
			</Header>

			<EmptyArea style={minHeight ? { minHeight } : undefined}>
				<Stack spacing={2} alignItems="center" textAlign="center">
					<IconRing variant="circular">
						<TrendingUpRounded fontSize="large" />
					</IconRing>

					<Typography variant="subtitle1" fontWeight={600}>
						No activity yet
					</Typography>

					<Typography
						variant="body2"
						color="#717182"
						sx={{ maxWidth: 560 }}
						fontWeight={500}
					>
						Your deposits, withdrawals, and earnings will appear
						here.
					</Typography>

					<ProTip>
						<Stack
							direction="row"
							spacing={1.25}
							alignItems="flex-start"
						>
							{/*<LightbulbOutlined fontSize="small" />*/}
							<Typography variant="body2" color="#000">
								<Strong>💡 Pro tip:</Strong> Start with a small
								deposit to see how everything works. You can
								always add more later!
							</Typography>
						</Stack>
					</ProTip>
				</Stack>
			</EmptyArea>
		</Root>
	);
};
